import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp } from 'lucide-react';
import { LiksTooltip } from '../LiksTooltip/LiksTooltip';
import { LiksMenuPanel } from './LiksMenuPanel';
import type { LiksMenuProps } from './LiksMenu.types';
import './LiksMenu.css';

export type {
  LiksMenuItem,
  LiksMenuPlacement,
  LiksMenuProps,
} from './LiksMenu.types';

export function LiksMenu({
  trigger,
  items,
  placement = 'up',
  isOpen,
  onOpenChange,
  onTriggerClick,
  showChevron = true,
  toggleTooltip,
  className,
  triggerClassName,
  panelClassName,
  itemClassName,
  activeItemClassName,
  portalPanel = false,
  matchTriggerWidth = false,
  'aria-label': ariaLabel,
}: LiksMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const visibleItems = items.filter((item) => !item.hidden);

  const updatePanelPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const panelHeight = panelRef.current?.offsetHeight ?? 132;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUpward =
      placement === 'up' ||
      (placement === 'down' &&
        spaceBelow < panelHeight &&
        rect.top > panelHeight + gap);
    const widthStyle =
      matchTriggerWidth && portalPanel
        ? { width: rect.width, minWidth: rect.width }
        : {};

    if (openUpward) {
      setPanelStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + gap,
        right: window.innerWidth - rect.right,
        left: 'auto',
        top: 'auto',
        zIndex: 'var(--z-popover)',
        ...widthStyle,
      });
      return;
    }

    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + gap,
      right: window.innerWidth - rect.right,
      left: 'auto',
      bottom: 'auto',
      zIndex: 'var(--z-popover)',
      ...widthStyle,
    });
  }, [matchTriggerWidth, placement, portalPanel]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }

      onOpenChange(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen || !portalPanel) {
      return;
    }

    updatePanelPosition();

    const frame = window.requestAnimationFrame(() => {
      updatePanelPosition();
    });

    const handleReposition = () => {
      updatePanelPosition();
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, portalPanel, updatePanelPosition]);

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (onTriggerClick) {
      onTriggerClick(event);
      return;
    }

    onOpenChange(!isOpen);
  };

  const handleItemAction = (action?: () => void) => {
    action?.();
    onOpenChange(false);
  };

  const chevronClassName = [
    'liks-menu__chevron',
    `liks-menu__chevron--placement-${placement}`,
    isOpen && 'liks-menu__chevron--open',
  ]
    .filter(Boolean)
    .join(' ');

  const panelContent = isOpen && visibleItems.length > 0 && (
    <LiksMenuPanel
      panelRef={panelRef}
      placement={placement}
      portalPanel={portalPanel}
      panelClassName={panelClassName}
      panelStyle={panelStyle}
      items={visibleItems}
      itemClassName={itemClassName}
      activeItemClassName={activeItemClassName}
      onItemAction={handleItemAction}
    />
  );

  const panel =
    panelContent &&
    (portalPanel && typeof document !== 'undefined'
      ? createPortal(panelContent, document.body)
      : panelContent);

  return (
    <div
      ref={menuRef}
      className={['liks-menu', className].filter(Boolean).join(' ')}
    >
      <LiksTooltip content={toggleTooltip} placement="right" block>
        <button
          ref={triggerRef}
          type="button"
          className={['liks-menu__trigger', triggerClassName]
            .filter(Boolean)
            .join(' ')}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          onClick={handleTriggerClick}
        >
          <span className="liks-menu__trigger-content">{trigger}</span>
          {showChevron && (
            <span className="liks-menu__chevron-group" aria-hidden>
              <ChevronUp
                className={chevronClassName}
                strokeWidth={2}
                aria-hidden
              />
            </span>
          )}
        </button>
      </LiksTooltip>
      {panel}
    </div>
  );
}
